import API from '@api/API';
import { ENDPOINTS } from '@api/endpoints';
import {
  CreateCompanyDto,
  CreateCompanyResponse,
  DeleteCompanyResponse,
  GetCompanyParams,
  GetCompanyResponse,
  LegalFormsResponse,
  SetCompanyLegalFormDto,
  SetCompanyLegalFormResponse,
  ZefixCompanySearchResponse,
} from '@api/types/company';
import {
  TeamMember,
  UpdateTeamMemberPayload,
  UpdateTeamMemberSignatoryPayload,
} from '@api/types/team';

export const companyApi = {
  getCompany: async (
    id: string | number,
    params?: GetCompanyParams,
  ): Promise<GetCompanyResponse> => {
    const response = await API().get(ENDPOINTS.company.companyItem(id), { params });
    return response.data;
  },

  createCompany: async (body: CreateCompanyDto): Promise<CreateCompanyResponse> => {
    const response = await API().postForm(ENDPOINTS.company.create, body);
    return response.data;
  },

  updateCompany: async (id: number, body: CreateCompanyDto): Promise<void> => {
    const response = await API().postForm(ENDPOINTS.company.update(id), body);
    return response.data;
  },

  deleteCompany: async (id: string | number): Promise<DeleteCompanyResponse> => {
    const response = await API().delete(ENDPOINTS.company.companyItem(id));
    return response.data;
  },

  getLegalForms: async (): Promise<LegalFormsResponse> => {
    const response = await API().get(ENDPOINTS.company.legalForms);
    return response.data;
  },

  setLegalForm: async (
    id: string | number,
    body: SetCompanyLegalFormDto,
  ): Promise<SetCompanyLegalFormResponse> => {
    const response = await API().post(ENDPOINTS.company.legalForm(id), body);
    return response.data;
  },

  getCommercialRegisterURL: async (companyId: string | number): Promise<{ url: string }> => {
    const response = await API().get(ENDPOINTS.company.commercialRegister(companyId));
    return response.data;
  },

  inviteMember: async (id: number, body: { email: string; role: string }): Promise<TeamMember> => {
    const response = await API().post(ENDPOINTS.company.teamMembers(id), body);
    return response.data;
  },

  changeMemberRole: async (
    id: number,
    memberId: number,
    body: UpdateTeamMemberPayload,
  ): Promise<{ teamMember: TeamMember }> => {
    const response = await API().post(ENDPOINTS.company.memberChangeRole(id, memberId), body);
    return response.data;
  },

  changeMemberSignatory: async (
    id: number,
    memberId: number,
    body: UpdateTeamMemberSignatoryPayload,
  ): Promise<{ teamMember: TeamMember }> => {
    const response = await API().post(ENDPOINTS.company.memberSignatory(id, memberId), body);
    return response.data;
  },

  removeMember: async (id: number, memberId: number): Promise<void> => {
    const response = await API().delete(ENDPOINTS.company.memberDelete(id, memberId));
    return response.data;
  },

  fetchZefixCompanies: async (
    query: string,
    options: { signal?: AbortSignal } = {},
  ): Promise<ZefixCompanySearchResponse> => {
    const { signal } = options;
    const response = await API().get(ENDPOINTS.company.zefix(), {
      params: { query },
      signal,
    });
    return response.data;
  },
};
